import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { authClient } from '../lib/auth';
import { useAuthStore } from '../stores/authStore';

type Mode = 'totp' | 'backup';

export function TwoFactorChallengePage() {
  const [mode, setMode] = useState<Mode>('totp');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const checkSession = useAuthStore((s) => s.checkSession);

  const handleVerify = async () => {
    setError(null);
    if (!code.trim()) return;
    setLoading(true);
    try {
      let result;
      if (mode === 'totp') {
        result = await (authClient as any).twoFactor.verifyTotp({ code: code.trim() });
      } else {
        result = await (authClient as any).twoFactor.verifyBackupCode({ code: code.trim() });
      }
      if (result?.error) {
        setError(result.error.message ?? 'Invalid code. Please try again.');
        return;
      }
      await checkSession();
      navigate('/dashboard');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Card sx={{ width: '100%', maxWidth: 400 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" fontWeight={700} mb={1}>
            Two-factor authentication
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            {mode === 'totp'
              ? 'Enter the 6-digit code from your authenticator app.'
              : 'Enter one of your backup codes.'}
          </Typography>

          <TextField
            label={mode === 'totp' ? 'Authenticator code' : 'Backup code'}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { void handleVerify(); } }}
            fullWidth
            autoFocus
            autoComplete="one-time-code"
            inputProps={{ maxLength: mode === 'totp' ? 6 : 20 }}
            sx={{ mb: 2 }}
          />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Button
            variant="contained"
            fullWidth
            onClick={() => { void handleVerify(); }}
            disabled={loading || !code.trim()}
            startIcon={loading ? <CircularProgress size={16} /> : undefined}
            sx={{ mb: 2 }}
          >
            Verify
          </Button>

          <Box display="flex" justifyContent="space-between">
            <Link
              component="button"
              variant="body2"
              onClick={() => {
                setMode(mode === 'totp' ? 'backup' : 'totp');
                setCode('');
                setError(null);
              }}
            >
              {mode === 'totp' ? 'Use a backup code instead' : 'Use authenticator app instead'}
            </Link>
            <Link
              component="button"
              variant="body2"
              color="text.secondary"
              onClick={() => navigate('/login')}
            >
              Back to login
            </Link>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
